import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Loader2, CheckCircle2, Send, Building2, User, Clock, Sparkles, DollarSign, AlertTriangle, BadgeCheck, Lightbulb } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../utils/api';
import { CHILEAN_REGIONS } from '../constants/chileanLocations';

var CENTER_MODALITIES = [
  { value: 'residencial', label: 'Residencial (internación 24/7)' },
  { value: 'ambulatorio', label: 'Ambulatorio (sesiones regulares)' },
  { value: 'online', label: 'Online / Telemedicina' },
  { value: 'gratis', label: 'Gratis (SENDA/FONASA) ⚠️', warn: true }
];

var PROFESSIONAL_TYPES = [
  { value: 'psicologo', label: 'Psicólogo/a' },
  { value: 'psiquiatra', label: 'Psiquiatra' },
  { value: 'terapeuta', label: 'Terapeuta en adicciones' },
  { value: 'consejero', label: 'Consejero/a en adicciones' },
  { value: 'gratis', label: 'Gratis (SENDA/FONASA) ⚠️', warn: true }
];

var ADDICTIONS = [
  'Alcohol', 'Cocaína', 'Pasta Base', 'Marihuana', 'Benzodiacepinas / Medicamentos',
  'Ludopatía', 'Adicciones conductuales', 'Patología dual', 'Otras'
];

var URGENCY = [
  { value: 'inmediata', label: 'Inmediata (hoy / mañana)' },
  { value: 'esta_semana', label: 'Esta semana' },
  { value: 'flexible', label: 'Flexible (2-4 semanas)' }
];

// Rangos de presupuesto por modalidad
var BUDGET_RANGES = {
  residencial: [
    { value: '350-450', label: '$350k – $450k · básico' },
    { value: '450-750', label: '$450k – $750k · intermedio' },
    { value: '750-1000', label: '$750k – $1M · alto estándar' },
    { value: '1000+', label: '+$1M · premium' }
  ],
  ambulatorio: [
    { value: '250-450', label: '$250k – $450k · básico' },
    { value: '450-650', label: '$450k – $650k · intermedio' },
    { value: '700+', label: '+$700k · alto estándar' }
  ],
  online: [
    { value: '150-250', label: '$150k – $250k · básico' },
    { value: '250-350', label: '$250k – $350k · regular' },
    { value: '400+', label: '+$400k · intensivo' }
  ],
  psicologo: [
    { value: 'consulta', label: 'Consulta ($30k – $50k)' },
    { value: 'paquete', label: 'Paquete 4 sesiones ($100k – $200k)' },
    { value: 'mensual', label: 'Programa mensual (+$200k)' }
  ]
};
BUDGET_RANGES.psiquiatra = BUDGET_RANGES.psicologo;
BUDGET_RANGES.terapeuta = BUDGET_RANGES.psicologo;
BUDGET_RANGES.consejero = BUDGET_RANGES.psicologo;

export default function QuoteRequestFloating() {
  var location = useLocation();
  var navigate = useNavigate();
  var [open, setOpen] = useState(false);
  var [step, setStep] = useState(1);
  var [loading, setLoading] = useState(false);
  var [done, setDone] = useState(null);
  var [matchCount, setMatchCount] = useState(null);
  var [showGratisModal, setShowGratisModal] = useState(false);
  var [form, setForm] = useState({
    target_type: 'center',
    region: '',
    modality: '',
    addiction: '',
    urgency: '',
    budget_range: '',
    can_use_medical_license: false,
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  // Hide on dashboards / auth / admin
  var hiddenPrefixes = ['/dashboard', '/admin', '/sales-dashboard', '/login', '/auth', '/register', '/profile', '/checkout', '/payment', '/webpay', '/professional-dashboard', '/workshop-dashboard', '/center-dashboard', '/center/', '/professional/', '/workshop/', '/ventas', '/experiencia', '/yo-del-futuro', '/espejo', '/tratamiento-adicciones-online', '/encuesta/', '/coach-adicciones'];
  var hidden = hiddenPrefixes.some(function(p) { return location.pathname.startsWith(p); });

  // Live match count when region/modality changes
  useEffect(function() {
    if (!open || step < 2) return;
    if (!form.region && !form.modality) { setMatchCount(null); return; }
    var params = new URLSearchParams();
    params.set('target_type', form.target_type);
    if (form.region) params.set('region', form.region);
    if (form.modality) params.set('modality', form.modality);
    axios.get(API + '/quote-requests/match-count?' + params.toString())
      .then(function(r) { setMatchCount(r.data.count || 0); })
      .catch(function() { setMatchCount(null); });
  }, [form.region, form.modality, form.target_type, open, step]);

  function update(k, v) { setForm(function(f) { var nf = Object.assign({}, f); nf[k] = v; return nf; }); }

  async function submit() {
    setLoading(true);
    try {
      var sourcePath = (typeof window !== 'undefined' && window.location) ? window.location.pathname : '';
      // Attach UTM params (from URL or sessionStorage 'sa_utm')
      var utm = {};
      try {
        var url = new URL(window.location.href);
        ['utm_source','utm_medium','utm_campaign'].forEach(function(k){ var v = url.searchParams.get(k); if (v) utm[k] = v; });
        if (Object.keys(utm).length === 0) {
          var stored = JSON.parse(sessionStorage.getItem('sa_utm') || 'null');
          if (stored && stored.data) utm = stored.data;
        }
      } catch (e) { /* noop */ }
      var payload = Object.assign({}, form, { source: 'floating-widget@' + sourcePath }, utm);
      var res = await axios.post(API + '/quote-requests', payload);
      setDone(res.data);
      // GA4 dataLayer push (vía services/analytics.js → mapea a generate_lead conversion)
      try {
        const a = await import('../services/analytics');
        a.trackEvent('cotizacion_submitted', 'quote_request', form.target_type);
      } catch (e) { /* noop */ }
      // Backward compat con configs viejas de gtag directo
      if (typeof window !== 'undefined' && window.gtag) {
        try { window.gtag('event', 'quote_request_submitted', { event_category: 'lead', event_label: form.target_type, value: res.data.matched_count }); } catch(e) {}
      }
    } catch (e) {
      setDone({ success: false, message: 'Error enviando la solicitud. Intenta nuevamente.' });
    }
    setLoading(false);
  }

  function close() {
    setOpen(false);
    setTimeout(function() {
      setStep(1);
      setDone(null);
      setMatchCount(null);
      setForm({
        target_type: 'center', region: '', modality: '', addiction: '', urgency: '',
        budget_range: '', can_use_medical_license: false,
        name: '', phone: '', email: '', message: ''
      });
    }, 300);
  }

  var modalities = form.target_type === 'center' ? CENTER_MODALITIES : PROFESSIONAL_TYPES;
  var canNext1 = form.target_type && form.region;
  var canNext2 = form.modality && form.modality !== 'gratis' && form.budget_range;
  var canSubmit = form.name.trim() && form.phone.trim();
  var budgetOptions = BUDGET_RANGES[form.modality] || [];

  function handleModalityClick(value) {
    if (value === 'gratis') {
      setShowGratisModal(true);
      return;
    }
    update('modality', value);
    update('budget_range', '');
  }

  if (hidden) return null;

  return (
    <>
      {/* Floating trigger button - bottom-right, above Fonodrogas */}
      {!open && (
        <button
          onClick={function() { setOpen(true); }}
          data-testid="quote-floating-btn"
          aria-label="Solicitar cotización"
          className="fixed bottom-6 left-4 z-[55] bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-2xl flex items-center gap-2 px-4 py-3 transition-all hover:scale-105 ring-2 ring-blue-500/30 ring-offset-2 ring-offset-slate-950 group"
        >
          <MessageSquare className="w-5 h-5" />
          <span className="text-sm font-semibold hidden sm:inline">Cotizar en 2 min</span>
          <span className="text-sm font-semibold sm:hidden">Cotizar</span>
          <span className="absolute -top-1 -right-1 bg-amber-400 text-slate-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full">GRATIS</span>
        </button>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200" onClick={close} data-testid="quote-modal">
          <div
            onClick={function(e) { e.stopPropagation(); }}
            className="bg-slate-900 border border-slate-800 rounded-t-3xl sm:rounded-3xl w-full max-w-xl max-h-[95vh] overflow-y-auto shadow-2xl animate-in slide-in-from-bottom-4 duration-300"
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-900/50 to-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-bold text-slate-50 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-400" />
                  Cotización en 2 minutos
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">Recibe respuesta de varios centros para comparar</p>
              </div>
              <button onClick={close} aria-label="Cerrar" className="text-slate-400 hover:text-slate-200" data-testid="quote-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {done ? (
              <div className="p-8 text-center">
                {done.success ? (
                  <>
                    <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-50 mb-2" data-testid="quote-success-title">¡Solicitud enviada!</h3>
                    <p className="text-slate-300 mb-4">{done.message}</p>
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6">
                      <p className="text-sm text-slate-400">
                        Tu solicitud fue enviada por <span className="text-emerald-400 font-bold">email</span> a <span className="text-emerald-400 font-bold">{done.matched_count}</span> {form.target_type === 'center' ? 'centros' : 'profesionales'} de la Región {form.region}{done.emails_sent ? ' (' + done.emails_sent + ' confirmados)' : ''}.
                        Te contactarán directamente por <span className="text-slate-200 font-medium">WhatsApp y email</span> con sus propuestas en las próximas 24 horas.
                      </p>
                    </div>
                    <button onClick={close} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-semibold transition" data-testid="quote-done-close">
                      Cerrar
                    </button>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center mx-auto mb-4">
                      <X className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-50 mb-2">Algo salió mal</h3>
                    <p className="text-slate-400 mb-6">{done.message}</p>
                    <button onClick={function() { setDone(null); setStep(2); }} className="bg-slate-800 hover:bg-slate-700 text-white py-3 px-6 rounded-lg font-semibold transition">
                      Intentar de nuevo
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="p-6">
                {/* Progress */}
                <div className="flex gap-2 mb-6">
                  {[1, 2, 3].map(function(s) {
                    return <div key={s} className={'h-1.5 flex-1 rounded-full transition ' + (s <= step ? 'bg-blue-500' : 'bg-slate-800')}></div>;
                  })}
                </div>

                {/* Step 1: target_type + region */}
                {step === 1 && (
                  <div className="space-y-5" data-testid="quote-step-1">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">¿Qué estás buscando?</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={function() { update('target_type', 'center'); update('modality', ''); }}
                          data-testid="quote-target-center"
                          className={'p-4 rounded-xl border-2 transition text-left ' + (form.target_type === 'center' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600 bg-slate-800/30')}
                        >
                          <Building2 className={'w-5 h-5 mb-2 ' + (form.target_type === 'center' ? 'text-blue-400' : 'text-slate-400')} />
                          <p className="font-semibold text-slate-50 text-sm">Centro de rehabilitación</p>
                          <p className="text-xs text-slate-400 mt-1">Residencial, ambulatorio u online</p>
                        </button>
                        <button
                          onClick={function() { update('target_type', 'professional'); update('modality', ''); }}
                          data-testid="quote-target-pro"
                          className={'p-4 rounded-xl border-2 transition text-left ' + (form.target_type === 'professional' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600 bg-slate-800/30')}
                        >
                          <User className={'w-5 h-5 mb-2 ' + (form.target_type === 'professional' ? 'text-blue-400' : 'text-slate-400')} />
                          <p className="font-semibold text-slate-50 text-sm">Profesional</p>
                          <p className="text-xs text-slate-400 mt-1">Psicólogo, psiquiatra, terapeuta</p>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">¿En qué región?</label>
                      <select
                        value={form.region}
                        onChange={function(e) { update('region', e.target.value); }}
                        data-testid="quote-region"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500"
                      >
                        <option value="">Selecciona una región…</option>
                        {CHILEAN_REGIONS.map(function(r) {
                          return <option key={r.value} value={r.value}>{r.label}</option>;
                        })}
                      </select>
                    </div>
                    <button
                      onClick={function() { if (canNext1) setStep(2); }}
                      disabled={!canNext1}
                      data-testid="quote-next-1"
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-800 disabled:text-slate-500 text-white py-3 rounded-lg font-semibold transition"
                    >
                      Continuar
                    </button>
                  </div>
                )}

                {/* Step 2: modality + addiction + urgency */}
                {step === 2 && (
                  <div className="space-y-5" data-testid="quote-step-2">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        {form.target_type === 'center' ? 'Modalidad de tratamiento' : 'Tipo de profesional'}
                      </label>
                      <div className="space-y-2">
                        {modalities.map(function(m) {
                          var isWarn = m.warn;
                          var isSel = form.modality === m.value;
                          return (
                            <button
                              key={m.value}
                              onClick={function() { handleModalityClick(m.value); }}
                              data-testid={'quote-modality-' + m.value}
                              className={'w-full p-3 rounded-lg border text-left text-sm transition ' + (isSel ? 'border-blue-500 bg-blue-500/10 text-slate-50' : isWarn ? 'border-amber-500/40 bg-amber-500/5 text-amber-200 hover:border-amber-500/60' : 'border-slate-700 hover:border-slate-600 bg-slate-800/30 text-slate-300')}
                            >
                              {m.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {matchCount !== null && (
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-sm text-emerald-300" data-testid="quote-match-preview">
                        <Sparkles className="w-4 h-4 inline mr-1" />
                        <strong>{matchCount}</strong> {form.target_type === 'center' ? 'centros' : 'profesionales'} recibirán tu cotización
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Tipo de adicción</label>
                      <select
                        value={form.addiction}
                        onChange={function(e) { update('addiction', e.target.value); }}
                        data-testid="quote-addiction"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500"
                      >
                        <option value="">Selecciona (opcional)</option>
                        {ADDICTIONS.map(function(a) { return <option key={a} value={a}>{a}</option>; })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        <Clock className="w-4 h-4 inline mr-1" />Urgencia
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {URGENCY.map(function(u) {
                          return (
                            <button
                              key={u.value}
                              onClick={function() { update('urgency', u.value); }}
                              data-testid={'quote-urgency-' + u.value}
                              className={'p-2 rounded-lg border text-xs transition ' + (form.urgency === u.value ? 'border-blue-500 bg-blue-500/10 text-slate-50' : 'border-slate-700 hover:border-slate-600 bg-slate-800/30 text-slate-300')}
                            >
                              {u.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Presupuesto (widget) */}
                    {form.modality && form.modality !== 'gratis' && budgetOptions.length > 0 && (
                      <div data-testid="quote-budget-section">
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          <DollarSign className="w-4 h-4 inline mr-1 text-emerald-400" />
                          Presupuesto mensual <span className="text-rose-400">*</span>
                        </label>
                        <div className="space-y-1.5">
                          {budgetOptions.map(function(b) {
                            var sel = form.budget_range === b.value;
                            return (
                              <button
                                key={b.value}
                                onClick={function() { update('budget_range', b.value); }}
                                data-testid={'quote-budget-' + b.value}
                                className={'w-full p-2.5 rounded-lg border-2 text-left text-xs transition ' + (sel ? 'border-emerald-500 bg-emerald-500/10 text-slate-50' : 'border-slate-700 hover:border-slate-600 text-slate-300')}
                              >
                                {b.label}
                              </button>
                            );
                          })}
                          <button
                            onClick={function() { update('budget_range', 'not-specified'); }}
                            data-testid="quote-budget-not-specified"
                            className={'w-full p-2.5 rounded-lg border-2 border-dashed text-left text-xs transition ' + (form.budget_range === 'not-specified' ? 'border-slate-400 bg-slate-800/60 text-slate-200' : 'border-slate-700 hover:border-slate-600 text-slate-400')}
                          >
                            Prefiero no indicar
                          </button>
                        </div>
                        <p className="text-[11px] text-blue-200/70 mt-2 flex gap-1"><Lightbulb className="w-3 h-3 flex-shrink-0 mt-0.5" /> A mayor precio = más atenciones y beneficios. Consulta el detalle en cada propuesta.</p>
                        <label className="mt-2 flex items-start gap-2 text-xs text-slate-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.can_use_medical_license}
                            onChange={function(e) { update('can_use_medical_license', e.target.checked); }}
                            data-testid="quote-license-checkbox"
                            className="mt-0.5 w-4 h-4 accent-emerald-500"
                          />
                          <span>Puedo pagar con <b>licencia médica psiquiátrica</b>.</span>
                        </label>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button onClick={function() { setStep(1); }} data-testid="quote-back-2" className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-3 rounded-lg font-semibold transition">
                        Atrás
                      </button>
                      <button
                        onClick={function() { if (canNext2) setStep(3); }}
                        disabled={!canNext2}
                        data-testid="quote-next-2"
                        className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-800 disabled:text-slate-500 text-white py-3 rounded-lg font-semibold transition"
                      >
                        Continuar
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: contact info */}
                {step === 3 && (
                  <div className="space-y-4" data-testid="quote-step-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Tu nombre</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={function(e) { update('name', e.target.value); }}
                        placeholder="Nombre completo"
                        data-testid="quote-name"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">WhatsApp / Teléfono</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={function(e) { update('phone', e.target.value); }}
                        placeholder="+56 9 XXXX XXXX"
                        data-testid="quote-phone"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Email (opcional)</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={function(e) { update('email', e.target.value); }}
                        placeholder="tu@email.com"
                        data-testid="quote-email"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Mensaje adicional (opcional)</label>
                      <textarea
                        value={form.message}
                        onChange={function(e) { update('message', e.target.value); }}
                        placeholder="Cuéntanos detalles que ayuden a los centros a enviarte la mejor propuesta"
                        rows={3}
                        data-testid="quote-message"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 resize-none"
                      />
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Al enviar aceptas que tus datos sean compartidos con los {form.target_type === 'center' ? 'centros' : 'profesionales'} matching de la Región de {form.region} para que puedan ofrecerte su propuesta. Nunca compartiremos tus datos con fines comerciales.
                    </p>
                    <div className="flex gap-3">
                      <button onClick={function() { setStep(2); }} data-testid="quote-back-3" disabled={loading} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-3 rounded-lg font-semibold transition disabled:opacity-50">
                        Atrás
                      </button>
                      <button
                        onClick={submit}
                        disabled={!canSubmit || loading}
                        data-testid="quote-submit"
                        className="flex-[2] bg-blue-600 hover:bg-blue-700 disabled:bg-slate-800 disabled:text-slate-500 text-white py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <><Loader2 className="w-4 h-4 animate-spin" />Enviando…</>
                        ) : (
                          <><Send className="w-4 h-4" />Enviar cotización</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de advertencia si escoge Gratis (widget) */}
      {showGratisModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={function() { setShowGratisModal(false); }}
          data-testid="quote-gratis-modal-backdrop"
        >
          <div
            className="w-full max-w-md bg-slate-900 border border-amber-500/40 rounded-2xl shadow-2xl overflow-hidden"
            onClick={function(e) { e.stopPropagation(); }}
            data-testid="quote-gratis-modal"
          >
            <div className="relative bg-gradient-to-br from-amber-500/20 to-slate-900 p-4 border-b border-amber-500/30">
              <button onClick={function() { setShowGratisModal(false); }} className="absolute top-3 right-3 text-slate-400 hover:text-slate-100" aria-label="Cerrar">
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                <h3 className="text-base font-bold text-slate-50 leading-tight">Antes de continuar con centros gratuitos</h3>
              </div>
            </div>
            <div className="p-4 space-y-3 text-xs">
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-2.5">
                <div className="font-semibold text-rose-200 mb-1 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Listas de espera: 1 a 5 meses</div>
                <p className="text-slate-300">En crisis, esperar meses puede no ser recomendable.</p>
              </div>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2.5">
                <div className="font-semibold text-emerald-200 mb-1 flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5" /> Licencia médica psiquiátrica</div>
                <p className="text-slate-300">Cubre parte del tratamiento privado vía ISAPRE/FONASA.</p>
              </div>
              <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-2.5">
                <div className="font-semibold text-blue-200 mb-1 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Cotizar privado es gratis</div>
                <p className="text-slate-300">Recibe hasta 20 propuestas con precios y disponibilidad.</p>
              </div>
            </div>
            <div className="p-4 pt-0 space-y-2">
              <button
                onClick={function() { setShowGratisModal(false); update('modality', ''); }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-semibold text-sm"
                data-testid="quote-gratis-modal-cotizar-privado"
              >
                Prefiero cotizar centros privados
              </button>
              <button
                onClick={function() {
                  setShowGratisModal(false);
                  try { if (typeof window !== 'undefined' && window.gtag) { window.gtag('event', 'cotizador_gratis_redirect', { event_category: 'cotizacion', source: 'floating-widget', region: form.region || 'no_region' }); } } catch (_gtagErr) { /* noop */ }
                  navigate('/centros-de-rehabilitacion-de-drogas-gratis');
                }}
                className="w-full py-2 text-[11px] text-slate-400 hover:text-slate-200 underline"
                data-testid="quote-gratis-modal-continue"
              >
                Igual quiero ver la guía de centros gratuitos →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
